import { FC, useEffect, useState } from 'react';
import { Form } from 'src/models/form';
import { useFormAnswerHandler } from 'src/context/FormAnswerHandlerContext';
import AnswersContext from 'src/hooks/useAnswers';
import { useParams, useHistory } from 'react-router-dom';
import LoggedInUserContext from 'src/hooks/useLoggedInUser';
import { FormattedMessage, useIntl } from 'react-intl';
import { RoleTypes } from 'src/models/roles';
import FormAnswers from 'src/components/FormComponent/FormAnswers';
import FormQuestionGroupComponent from './FormQuestionGroupComponent/FormQuestionGroupComponent';
import ProgressBar from './ProgressBar/ProgressBar';
import FormButtons from './FormButtons/FormButtons';
import ClassesContext from '../../hooks/useClasses';
import Spinner from '../_common/Spinner/Spinner';
import FormAdminExpectationInfo from './FormAdminExpectationInfo/FormAdminExpectationInfo';
import GradingOptionLabel, { LabelType } from './FormQuestionComponent/FormQuestionTypePicker/FormQuestionGradingComponent/GradingOptionLabel';

export interface FormProps {
  form: Form,
  canDeliver?: boolean,
}

interface FormPageParams {
  formId: string,
  assignmentId?: string,
  studentUserId?: string,
  classId?: string,
}

export const FormComponent: FC<FormProps> = ({ form, canDeliver = true }: FormProps) => {
  const { formId, assignmentId, studentUserId, classId } = useParams<FormPageParams>();
  const { formatMessage } = useIntl();

  const history = useHistory();

  const [answers, setAnswers] = useState<FormAnswers>();
  const [adminMidTermExpectations, setAdminMidTermExpectations] = useState<FormAnswers>();
  const [adminEndTermExpectations, setAdminEndTermExpectations] = useState<FormAnswers>();
  const [teacherGrades, setTeacherGrades] = useState<FormAnswers>();
  const [teacherMidTermGrades, setTeacherMidTermGrades] = useState<FormAnswers>();
  const [supervisorGrades, setSupervisorGrades] = useState<FormAnswers>();
  const [studentGrades, setStudentGrades] = useState<FormAnswers>();
  const [pages, setPages] = useState<Array<number>>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [correctAssignmentId, setCorrectAssignmentId] = useState<string | undefined>(assignmentId);
  const { location } = history;

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const answerHandler = useFormAnswerHandler();

  const { user, activeRole } = LoggedInUserContext.useContainer();
  const { currentClass, getCurrentClass, currentClassLoaded } = ClassesContext.useContainer();
  const [midTermEvaluation, setMidTermEvaluation] = useState<boolean>();

  const {
    getGradedAnswerSetIds,
    gradedAnswerSetIds,
    gradedAnswerSetIdsLoaded,
    getRelevantAnswers,
    relevantAnswersLoaded,
    studentAnswers,
    teacherAnswers,
    supervisorAnswers,
    adminAnswers,
    midTermExpectations,
    endTermExpectations,
    setRelevantAnswersLoaded,
    getAdminAnswers,
    setGradedAnswerSetIdsLoaded,
    teacherMidTermAnswers,
    studentMidTermAnswers,
    supervisorMidTermAnswers,
    resetAnswersContext,
  } = AnswersContext.useContainer();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activePage]);

  useEffect(() => {
    if (classId) {
      if (currentClass?.id !== +classId) getCurrentClass(+classId);
      if (studentUserId) getGradedAnswerSetIds(+classId, +studentUserId, +formId);
      if (activeRole === RoleTypes.Student && user && midTermEvaluation !== undefined) {
        resetAnswersContext();
        getRelevantAnswers(+classId, user.id, +formId, midTermEvaluation);
        getGradedAnswerSetIds(+classId, user.id, +formId);
      }
    }
    return function cleanup() {
      resetAllAnswers();
    };
  }, [midTermEvaluation]);

  const resetAllAnswers = () => {
    setAnswers(undefined);
    setGradedAnswerSetIdsLoaded(false);
    setRelevantAnswersLoaded(false);
    setSupervisorGrades(undefined);
    setStudentGrades(undefined);
    setTeacherMidTermGrades(undefined);
    setTeacherGrades(undefined);
    setAdminMidTermExpectations(undefined);
    setAdminEndTermExpectations(undefined);
  };

  useEffect(() => {
    if (classId && midTermEvaluation !== undefined) {
      if (activeRole === RoleTypes.Admin) getAdminAnswers(+classId, +formId, midTermEvaluation);
    }
  }, [midTermEvaluation]);

  useEffect(() => {
    if (relevantAnswersLoaded) {
      if (adminAnswers?.formId === +formId) {
        setAnswers(new FormAnswers(form, adminAnswers));
      } else {
        setAnswers(new FormAnswers(form));
      }
    }
  }, [adminAnswers, relevantAnswersLoaded]);

  useEffect(() => {
    if (activeRole && gradedAnswerSetIdsLoaded && gradedAnswerSetIds && midTermEvaluation !== undefined) {
      if (classId && studentUserId) {
        resetAnswersContext();
        getRelevantAnswers(+classId, +studentUserId, +formId, midTermEvaluation);
      }
    }
  }, [activeRole, gradedAnswerSetIdsLoaded, gradedAnswerSetIds, midTermEvaluation]);

  useEffect(() => {
    if (relevantAnswersLoaded) {
      if (endTermExpectations?.formId === form.id) {
        if (activeRole !== RoleTypes.Admin) {
          setAdminEndTermExpectations(new FormAnswers(form, endTermExpectations));
          setAdminMidTermExpectations(new FormAnswers(form, midTermExpectations));
        }
      }
    }
  }, [endTermExpectations, midTermExpectations, relevantAnswersLoaded]);

  const correctAssignment = currentClass?.assignments?.find(assignment => correctAssignmentId && assignment.id === +correctAssignmentId);

  useEffect(() => {
    if (currentClassLoaded) {
      if (correctAssignment) setMidTermEvaluation(correctAssignment?.isMidSemester);
    }
  }, [currentClassLoaded]);

  useEffect(() => {
    if (relevantAnswersLoaded) {
      if (activeRole === RoleTypes.Student) {
        setSupervisorGrades(new FormAnswers(form, supervisorAnswers));
        setTeacherGrades(new FormAnswers(form, teacherAnswers));
        if (studentAnswers?.formId === +formId) {
          setAnswers(new FormAnswers(form, studentAnswers));
        } else {
          setAnswers(new FormAnswers(form));
        }
      }
      if (activeRole === RoleTypes.Advisor) {
        setStudentGrades(new FormAnswers(form, studentAnswers));
        setTeacherGrades(new FormAnswers(form, teacherAnswers));
        if (supervisorAnswers?.formId === +formId) {
          setAnswers(new FormAnswers(form, supervisorAnswers));
        } else {
          setAnswers(new FormAnswers(form));
        }
      }
      if (activeRole === RoleTypes.Teacher) {
        setSupervisorGrades(new FormAnswers(form, supervisorAnswers));
        setStudentGrades(new FormAnswers(form, studentAnswers));
        if (teacherAnswers?.formId === +formId) {
          setAnswers(new FormAnswers(form, teacherAnswers));
        } else {
          setAnswers(new FormAnswers(form));
        }
      }
      setTeacherMidTermGrades(new FormAnswers(form, teacherMidTermAnswers));
    }
  }, [relevantAnswersLoaded, gradedAnswerSetIdsLoaded, studentAnswers, teacherAnswers, supervisorAnswers, teacherMidTermAnswers]);

  useEffect(() => {
    setPages(Array.from(Array(form.groups.length), (_, index) => index + 1));
  }, [form]);

  const deliverAnswer = async () => {
    setIsSaving(true);
    let isSuccess = false;
    if (activeRole === RoleTypes.Admin && currentClass?.id) {
      isSuccess = await answerHandler.setExpectations(answers!.toAnswerSet(user?.id, undefined, undefined, currentClass?.subjectId, midTermEvaluation), currentClass?.id);
    } else {
      isSuccess = await answerHandler.deliver(answers!.toAnswerSet(user?.id, correctAssignmentId, studentUserId));
    }
    if (isSuccess) {
      const ifHasPath: string = (location.state as any)?.pPath;
      if (ifHasPath) {
        history.push(ifHasPath);
      }
    }
    setIsSaving(false);
  };

  const handleSetMidTermEvaluation = (value: boolean) => {
    const newAssignmentId = currentClass?.assignments?.find(assignment => {
      if (assignment.isMidSemester === value) {
        return { ...assignment, forms: assignment.forms.filter(_form => _form.id === +formId) };
      }
      return false;
    })?.id;
    if (newAssignmentId) {
      setCorrectAssignmentId(newAssignmentId.toString());
    }
    setMidTermEvaluation(value);
  };

  useEffect(() => {
    if (assignmentId && correctAssignmentId) {
      history.replace({ pathname: correctAssignmentId, state: location.state });
    }
  }, [correctAssignmentId]);

  if (canDeliver && (answers === undefined || !relevantAnswersLoaded)) return <Spinner />;

  const questionSchemeOrder = (groupOrder: number, questionOrder: number) => {
    let counter = 0;
    form.groups.map(group => {
      if (groupOrder >= group.order) {
        group.questions.map(question => {
          if (groupOrder !== group.order) {
            counter += 1;
          } else if (questionOrder >= question.order) {
            counter += 1;
          }
          return counter;
        });
      }
      return counter;
    });
    return counter;
  };

  if (form?.groups.length === 0) return <div><FormattedMessage id="no-form-groups" /></div>;

  return (
    <>
      {activeRole === RoleTypes.Admin && canDeliver && currentClass?.period
        && <FormAdminExpectationInfo formName={form.name} periodName={currentClass.period.name} isMainForm={form.isMainForm} midTermEvaluation={midTermEvaluation || false} setMidTermEvaluation={handleSetMidTermEvaluation} />}
      <div className="pb-4">
        <ProgressBar pages={pages} activePage={activePage} setActivePage={setActivePage} />
        <div className="flex pt-8 justify-center">
          <div>
            <p className="mr-4 text-xs xs:text-base">
              <FormattedMessage id="expected" />:
            </p>
          </div>
          <div className="mr-4">
            <GradingOptionLabel label={formatMessage({ id: 'mid-semester' })} type={LabelType.MidSemesterExpectation} />
          </div>
          <div>
            <GradingOptionLabel label={formatMessage({ id: 'end-semester' })} type={LabelType.EndSemesterExpectation} />
          </div>
        </div>
        <FormQuestionGroupComponent
          questionGroup={form?.groups[activePage - 1]}
          key={form?.groups[activePage - 1]?.id}
          answers={answers}
          midTermExpectations={adminMidTermExpectations}
          endTermExpectations={adminEndTermExpectations}
          studentGrades={studentGrades}
          studentMidTermGrades={studentMidTermAnswers !== undefined ? new FormAnswers(form, studentMidTermAnswers) : undefined}
          supervisorGrades={supervisorGrades}
          supervisorMidTermGrades={supervisorMidTermAnswers !== undefined ? new FormAnswers(form, supervisorMidTermAnswers) : undefined}
          teacherMidTermGrades={teacherMidTermGrades}
          teacherGrades={teacherGrades}
          isMidSemester={!!midTermEvaluation}
          questionSchemeOrder={questionSchemeOrder}
        />
      </div>
      <FormButtons setActivePage={setActivePage} deliverAnswer={deliverAnswer} activePage={activePage} pages={pages} canDeliver={canDeliver} isSaving={isSaving} />

    </>
  );
};

export default FormComponent;
